import {Platform} from 'react-native';
import {QueryClient} from '@tanstack/react-query';
import {persistQueryClient} from '@tanstack/react-query-persist-client';

import config from '../config';
import {logger} from '../utils/logger';

import {PERSIST_GC_TIME_MS, PERSIST_MAX_AGE_MS, shouldDehydrateQuery} from './persistConfig';
import {createPlatformPersister} from './persister';

/**
 * TanStack Query client with app-specific defaults.
 *
 * Persistence (DIS-1201):
 * - Imperative `persistQueryClient()` at module-load time so first paint never blocks.
 * - Web: localStorage (sync); Native: AsyncStorage (async). Selected via .web/.native file split.
 * - 24h max age, allow-list dehydrate (feed/brands/segments/departments/promotions).
 * - Buster: config.APP_VERSION (web webpack DefinePlugin || native MARKETING_VERSION || '0.0.0-dev').
 *
 * Configuration rationale:
 * - staleTime: 5min balances performance with data freshness
 * - gcTime: 24h required so persisted queries survive in memory long enough to be written
 * - retry: 2 aligns with existing circuit breaker patterns
 * - refetchOnWindowFocus: web-only for responsiveness without mobile battery drain
 * - refetchOnMount: handles store switching UX
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: PERSIST_GC_TIME_MS,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: Platform.OS === 'web',
      refetchOnReconnect: true,
      refetchOnMount: true,
      refetchInterval: false,
    },
    mutations: {
      retry: false,
    },
  },
});

try {
  const persister = createPlatformPersister();
  if (persister) {
    persistQueryClient({
      queryClient,
      persister,
      maxAge: PERSIST_MAX_AGE_MS,
      buster: config.APP_VERSION,
      dehydrateOptions: {shouldDehydrateQuery},
    });
  }
} catch (err) {
  logger.error('queryClient', 'Failed to initialise TanStack persist', {
    message: err instanceof Error ? err.message : String(err),
  });
}
